import { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import { FileStatus } from '../../common/types/form/form-fields/field-values';
import { asFilesValue } from '../../common/types/form/form-fields/field-values/utils';
import type { AttachmentUploadEndLifecycleEvent } from '../../common/types/lifecycle-handlers';
import { usePublicFormRendererActions } from '../form-renderer-store';

/**
 * Extracts analytics-safe attributes from an attachment-registration error for a consumer's
 * `ATTACHMENT_UPLOAD_END` handler. Handles both the help-center `ApiError` shape (`status`) and the
 * jira `FetchError`/`HttpError` shape (`statusCode`), plus `message`/`traceId` when present.
 */
export const getRegistrationErrorAttributes = (
	error: unknown,
): { error?: string; status?: string | number; traceId?: string } => {
	if (typeof error !== 'object' || error === null) {
		return {};
	}
	const message =
		'message' in error && typeof error.message === 'string' ? error.message : undefined;
	const status =
		'status' in error && (typeof error.status === 'string' || typeof error.status === 'number')
			? error.status
			: undefined;
	const statusCode =
		'statusCode' in error &&
		(typeof error.statusCode === 'string' || typeof error.statusCode === 'number')
			? error.statusCode
			: undefined;
	const traceId =
		'traceId' in error && typeof error.traceId === 'string' ? error.traceId : undefined;
	return { traceId, error: message, status: status ?? statusCode };
};

/**
 * Runs a consumer's "register uploaded file" step (performed inside their `ATTACHMENT_UPLOAD_END`
 * lifecycle handler, e.g. registering the media file with the project/Jira) and, if it fails,
 * removes the failed file from the field so it can't be silently submitted as a missing attachment
 * — clearing the field value when no files remain, mirroring the media upload error path. The
 * registration error is re-thrown so the consumer can surface its own product-specific messaging.
 *
 * NOTE: This intentionally does NOT manage the field's `fileStatus`. `fileStatus` is a single
 * field-level value (see `ModifiedFilesValue`) and the renderer already marks the field `UPLOADED`
 * on media upload-end, so holding it `UPLOADING` across registration would race across
 * concurrently-uploaded files: the first file to finish would clear the flag for the whole field
 * and prematurely re-enable the form's save/submit/send actions (`isFileUploadInProgress`). Proper
 * per-file upload/registration status should be tackled together with the attachment field upload
 * flow later. See JSDCLOUD-19146.
 */
export const useManageAttachmentRegistration = (): ((
	event: AttachmentUploadEndLifecycleEvent,
	register: () => Promise<unknown>,
) => Promise<void>) => {
	di(usePublicFormRendererActions);

	const { getFieldValues, updateModifiedValue } = usePublicFormRendererActions();

	return useCallback(
		async (event: AttachmentUploadEndLifecycleEvent, register: () => Promise<unknown>) => {
			const { fieldId } = event;
			const fileId = event.file.identifier.id;

			try {
				await register();
			} catch (error) {
				// Remove the file whose registration failed so it can't be silently submitted.
				const currentValue = asFilesValue(getFieldValues()[fieldId]);
				const remainingFiles =
					currentValue?.files.filter((file) => file.identifier.id !== fileId) ?? [];
				updateModifiedValue(
					fieldId,
					remainingFiles.length > 0
						? { files: remainingFiles, fileStatus: FileStatus.UPLOADED }
						: null,
				);

				throw error;
			}
		},
		[getFieldValues, updateModifiedValue],
	);
};
